// src/personalHabilitiesTests/PersonalHabilitiesTestBulkSend.js
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  Switch,
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  CloudUpload as UploadIcon,
  Psychology as PsychologyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  TableChart as TableChartIcon,
  CloudDone as CloudDoneIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TestReminderFields, { DEFAULT_REMINDER_CONFIG } from '../layouts/TestReminderFields';
import useStore from '../config/store';
import api from '../config/api';
import * as XLSX from 'xlsx';

const PersonalHabilitiesTestBulkSend = ({ applicants }) => {
  const { t } = useTranslation();
  const { authToken, project, organization, user, showAlert, language } = useStore();

  // Modo selección (manual vs excel)
  const [selectionMode, setSelectionMode] = useState('manual');
  const [selectedApplicants, setSelectedApplicants] = useState([]);

  // Estados Excel
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [fileError, setFileError] = useState(null);

  // Envío / job
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollingIntervalRef = useRef(null);

  const [selectedTests, setSelectedTests] = useState({
    integrityTest: false,
    emotionalIntelligence: false,
  });

  // Enviar email automáticamente al crear (igual que el envío individual)
  const [sendEmail, setSendEmail] = useState(true);
  const [reminderConfig, setReminderConfig] = useState(DEFAULT_REMINDER_CONFIG);

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleTestChange = (event) => {
    setSelectedTests({
      ...selectedTests,
      [event.target.name]: event.target.checked,
    });
  };

  const getSelectedTestTypes = () => Object.keys(selectedTests).filter((key) => selectedTests[key]);

  // Combinación manual + excel sin duplicados
  const getAllSelectedApplicants = () => {
    const fromExcel = previewData?.matched || [];
    const combined = [...selectedApplicants];

    fromExcel.forEach((excelApp) => {
      const excelId = excelApp.id || excelApp.uid;
      if (!combined.some((a) => (a.id || a.uid) === excelId)) {
        combined.push(excelApp);
      }
    });

    return combined;
  };

  // Excel
  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (!validTypes.includes(selectedFile.type)) {
      setFileError(t('personalHabilitiesTests.bulkSend.fileUpload.invalidFileType'));
      setFile(null);
      setPreviewData(null);
      return;
    }
    setFile(selectedFile);
    setFileError(null);
    processExcelFile(selectedFile);
  };

  const processExcelFile = (fileToProcess) => {
    setLoadingFile(true);
    setFileError(null);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setFileError(t('personalHabilitiesTests.bulkSend.fileUpload.emptyFile'));
          return;
        }

        const emails = jsonData
          .map((row) => {
            const emailKey = Object.keys(row).find((key) => key.toLowerCase() === 'email');
            return emailKey ? row[emailKey] : null;
          })
          .filter((email) => email && String(email).trim() !== '');

        if (emails.length === 0) {
          setFileError(t('personalHabilitiesTests.bulkSend.fileUpload.noEmailColumn'));
          return;
        }

        const matchedApplicants = [];
        const unmatchedEmails = [];

        emails.forEach((email) => {
          const emailNorm = String(email).toLowerCase();
          const applicant = applicants.find((a) => a.email && a.email.toLowerCase() === emailNorm);
          if (applicant) matchedApplicants.push(applicant);
          else unmatchedEmails.push(String(email));
        });

        setPreviewData({
          totalEmails: emails.length,
          matched: matchedApplicants,
          unmatched: unmatchedEmails,
        });
      } catch (error) {
        console.error('Error processing file:', error);
        setFileError(t('personalHabilitiesTests.bulkSend.fileUpload.processError') + ' ' + error.message);
      } finally {
        setLoadingFile(false);
      }
    };

    reader.onerror = () => {
      setFileError(t('personalHabilitiesTests.bulkSend.fileUpload.readError'));
      setLoadingFile(false);
    };

    reader.readAsArrayBuffer(fileToProcess);
  };

  const isBulkFormValid = () => {
    const allSelected = getAllSelectedApplicants();
    return allSelected.length > 0 && getSelectedTestTypes().length > 0;
  };

  const handleSendBulk = async () => {
    if (!isBulkFormValid()) {
      showAlert(t('personalHabilitiesTests.bulkSend.messages.loadValidCandidates'), 'warning');
      return;
    }

    const allSelected = getAllSelectedApplicants();
    const testTypes = getSelectedTestTypes();

    try {
      setSendingBulk(true);
      setFileError(null);

      const bulkData = {
        applicants: allSelected.map((a) => ({
          id: a.id || a.uid,
          name: a.name,
          email: a.email,
        })),
        testTypes,
        projectId: project,
        organizationId: organization,
        userId: user.uid,
        userEmail: user.email,
        language: language || 'es',
        sendEmail,
        reminderConfig,
      };

      const response = await api.post('/reasoning-tests/bulk', bulkData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.errorCode === 0) {
        const jobId = response.data.response.data.jobId;
        setBulkJobId(jobId);
        showAlert(
          `${t('personalHabilitiesTests.bulkSend.messages.bulkStarted')} ${allSelected.length} ${t(
            'personalHabilitiesTests.bulkSend.actions.candidates'
          ).toLowerCase()}`,
          'success'
        );
        startPolling(jobId);
      } else {
        showAlert(
          response.data.errorMessage || t('personalHabilitiesTests.bulkSend.messages.errorStartingBulk'),
          'error'
        );
        setSendingBulk(false);
      }
    } catch (error) {
      console.error('Error sending bulk tests:', error);
      const errorMessage =
        error.response?.data?.response?.message ||
        t('personalHabilitiesTests.bulkSend.messages.errorStartingBulk');
      showAlert(errorMessage, 'error');
      setSendingBulk(false);
    }
  };

  const startPolling = (jobId) => {
    checkJobStatus(jobId);

    const interval = setInterval(() => {
      checkJobStatus(jobId);
    }, 3000);

    pollingIntervalRef.current = interval;
  };

  const checkJobStatus = async (jobId) => {
    try {
      const response = await api.get(
        `/reasoning-tests/bulk/${jobId}/status`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        const status = response.data.response;
        setJobStatus(status);

        if (status.status === 'completed' || status.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setSendingBulk(false);

          if (status.status === 'completed') {
            showAlert(
              `${t('personalHabilitiesTests.bulkSend.messages.sendCompleted')} ${status.results?.succeeded || 0} ${t('personalHabilitiesTests.bulkSend.messages.successful')}, ${status.results?.failed || 0} ${t('personalHabilitiesTests.bulkSend.messages.failed')}`,
              'success'
            );
            resetForm();
          } else {
            showAlert(t('personalHabilitiesTests.bulkSend.messages.bulkFailed'), 'error');
          }
        }
      }
    } catch (error) {
      console.error('Error checking job status:', error);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreviewData(null);
    setFileError(null);
    setBulkJobId(null);
    setJobStatus(null);
    setSelectedApplicants([]);
    setSelectionMode('manual');
    setSelectedTests({
      integrityTest: false,
      emotionalIntelligence: false,
    });
    setSendEmail(true);
    setReminderConfig(DEFAULT_REMINDER_CONFIG);
  };

  const downloadTemplate = () => {
    const template = [{ email: 'juan.perez@example.com' }, { email: 'maria.garcia@example.com' }];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Candidatos');
    XLSX.writeFile(wb, 'plantilla_envio_masivo_tests.xlsx');
  };

  // UI helpers
  const renderSelectionModeToggle = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('personalHabilitiesTests.bulkSend.selectionMethod')}
      </Typography>
      <ToggleButtonGroup
        value={selectionMode}
        exclusive
        onChange={(e, newMode) => {
          if (newMode !== null) setSelectionMode(newMode);
        }}
        fullWidth
        disabled={sendingBulk}
      >
        <ToggleButton value="manual">
          <PersonAddIcon sx={{ mr: 1 }} />
          {t('personalHabilitiesTests.bulkSend.selectManually')}
        </ToggleButton>
        <ToggleButton value="excel">
          <TableChartIcon sx={{ mr: 1 }} />
          {t('personalHabilitiesTests.bulkSend.uploadExcel')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderManualSelector = () => (
    <Box sx={{ mb: 3 }}>
      <Autocomplete
        multiple
        options={applicants}
        getOptionLabel={(option) => `${option.name} (${option.email})`}
        value={selectedApplicants}
        onChange={(event, newValue) => setSelectedApplicants(newValue)}
        disabled={sendingBulk}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('personalHabilitiesTests.bulkSend.selectApplicants')}
            placeholder={t('personalHabilitiesTests.bulkSend.searchApplicants')}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.id || option.uid || index}
              label={option.name}
              {...getTagProps({ index })}
              color="primary"
            />
          ))
        }
        noOptionsText={t('personalHabilitiesTests.bulkSend.noApplicantsFound')}
      />

      {selectedApplicants.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('personalHabilitiesTests.bulkSend.applicantsSelected', { count: selectedApplicants.length })}
        </Alert>
      )}
    </Box>
  );

  const renderExcelUpload = () => (
    <Box sx={{ mb: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          <strong>{t('personalHabilitiesTests.bulkSend.instructions.title')}</strong>
        </Typography>
        <Typography variant="body2" component="div">
          1. {t('personalHabilitiesTests.bulkSend.instructions.step1')}
          <br />
          2. {t('personalHabilitiesTests.bulkSend.instructions.step2')}
          <br />
          3. {t('personalHabilitiesTests.bulkSend.instructions.step3')}
        </Typography>
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={{ mt: 1 }}>
          {t('personalHabilitiesTests.bulkSend.instructions.downloadTemplate')}
        </Button>
      </Alert>

      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: fileError ? 'error.main' : 'divider',
          backgroundColor: loadingFile ? 'action.hover' : 'background.paper',
          cursor: loadingFile || sendingBulk ? 'wait' : 'pointer',
          '&:hover': {
            borderColor: loadingFile || sendingBulk ? 'divider' : 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => !loadingFile && !sendingBulk && document.getElementById('personal-file-input').click()}
      >
        <input
          id="personal-file-input"
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={loadingFile || sendingBulk}
        />

        {loadingFile ? (
          <Box>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body2">{t('personalHabilitiesTests.bulkSend.fileUpload.processing')}</Typography>
          </Box>
        ) : file ? (
          <Box>
            <CloudDoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="body1" gutterBottom>{file.name}</Typography>
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setPreviewData(null);
                setFileError(null);
              }}
            >
              {t('remove')}
            </Button>
          </Box>
        ) : (
          <Box>
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {t('personalHabilitiesTests.bulkSend.fileUpload.dragOrClick')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('personalHabilitiesTests.bulkSend.fileUpload.supportedFormats')}
            </Typography>
          </Box>
        )}
      </Paper>

      {fileError && (
        <Alert severity="error" sx={{ mt: 2 }}>{fileError}</Alert>
      )}
    </Box>
  );

  const renderCombinedPreview = () => {
    const allSelected = getAllSelectedApplicants();
    if (allSelected.length === 0) return null;

    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('personalHabilitiesTests.bulkSend.preview.summary')}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>{t('personalHabilitiesTests.bulkSend.preview.candidatesFound')}:</strong> {allSelected.length}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          {t('personalHabilitiesTests.bulkSend.preview.candidatesList')}
        </Typography>

        <List dense>
          {allSelected.slice(0, 5).map((applicant, idx) => (
            <ListItem key={applicant.id || applicant.uid || idx}>
              <ListItemText primary={applicant.name} secondary={applicant.email} />
            </ListItem>
          ))}
        </List>

        {allSelected.length > 5 && (
          <Typography variant="caption" color="text.secondary">
            {t('personalHabilitiesTests.bulkSend.preview.andMore')} {allSelected.length - 5}{' '}
            {t('personalHabilitiesTests.bulkSend.preview.moreRecords')}
          </Typography>
        )}

        {previewData?.unmatched?.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>{t('personalHabilitiesTests.bulkSend.preview.notFoundEmails')}</strong>
            </Typography>
            <Typography variant="caption">
              {previewData.unmatched.slice(0, 5).join(', ')}
              {previewData.unmatched.length > 5 && ` (+${previewData.unmatched.length - 5})`}
            </Typography>
          </Alert>
        )}
      </Paper>
    );
  };

  // Derivados
  const allSelected = getAllSelectedApplicants();
  const selectedTypes = getSelectedTestTypes();

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('personalHabilitiesTests.bulkSend.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('personalHabilitiesTests.bulkSend.subtitle')}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Toggle Manual / Excel */}
      {renderSelectionModeToggle()}

      {/* Selector manual */}
      {selectionMode === 'manual' && renderManualSelector()}

      {/* Carga Excel */}
      {selectionMode === 'excel' && renderExcelUpload()}

      {/* Preview combinado */}
      {renderCombinedPreview()}

      {/* Selector de tests */}
      {allSelected.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1">
              {t('personalHabilitiesTests.bulkSend.selectTests')}
            </Typography>
          </Box>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTests.integrityTest}
                  onChange={handleTestChange}
                  name="integrityTest"
                  disabled={sendingBulk}
                />
              }
              label={t('personalHabilitiesTests.integrityTest')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTests.emotionalIntelligence}
                  onChange={handleTestChange}
                  name="emotionalIntelligence"
                  disabled={sendingBulk}
                />
              }
              label={t('personalHabilitiesTests.emotionalIntelligence')}
            />
          </FormGroup>

          {selectedTypes.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('personalHabilitiesTests.bulkSend.selectedTests')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {selectedTypes.map((testType) => (
                  <Chip
                    key={testType}
                    label={t(`personalHabilitiesTests.${testType}`)}
                    color="secondary"
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Switch enviar email (mismo patrón que el envío individual) */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, gap: 1 }}>
            <EmailIcon sx={{ color: sendEmail ? 'primary.main' : 'action.disabled' }} />
            <FormControlLabel
              control={
                <Switch
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  color="primary"
                  disabled={sendingBulk}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('personalHabilitiesTests.sendEmailOnCreate')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sendEmail
                      ? t('personalHabilitiesTests.sendEmailOnCreateEnabled')
                      : t('personalHabilitiesTests.sendEmailOnCreateDisabled')
                    }
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Recordatorio automatico: solo aplica cuando el correo lo manda el sistema */}
          <TestReminderFields
            show={sendEmail}
            value={reminderConfig}
            onChange={setReminderConfig}
          />
        </Box>
      )}

      {/* Progreso del envío */}
      {sendingBulk && jobStatus && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info">
            <Typography variant="body2" gutterBottom>
              <strong>{t('personalHabilitiesTests.bulkSend.progress.title')}</strong> {jobStatus.status}
            </Typography>
            <LinearProgress variant="determinate" value={jobStatus.progress || 0} sx={{ my: 1 }} />
            <Typography variant="caption">
              {t('personalHabilitiesTests.bulkSend.progress.progressLabel')} {jobStatus.progress || 0}%
            </Typography>
            {jobStatus.results && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {t('personalHabilitiesTests.bulkSend.progress.processed')} {jobStatus.results.processed || 0}/
                {jobStatus.results.total || 0} | {t('personalHabilitiesTests.bulkSend.progress.successful')}{' '}
                {jobStatus.results.succeeded || 0} | {t('personalHabilitiesTests.bulkSend.progress.failed')}{' '}
                {jobStatus.results.failed || 0}
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* Botones */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={resetForm} disabled={sendingBulk}>
          {t('personalHabilitiesTests.bulkSend.actions.cancel')}
        </Button>

        <Button
          variant="contained"
          startIcon={sendingBulk ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSendBulk}
          disabled={sendingBulk || !isBulkFormValid()}
          size="large"
        >
          {sendingBulk
            ? t('personalHabilitiesTests.bulkSend.actions.sending')
            : sendEmail
              ? `${t('personalHabilitiesTests.bulkSend.actions.sendTo')} ${allSelected.length} ${t(
                'personalHabilitiesTests.bulkSend.actions.candidates'
              )}`
              : `${t('personalHabilitiesTests.createTests')} (${allSelected.length})`}
        </Button>
      </Box>
    </Paper>
  );
};

export default PersonalHabilitiesTestBulkSend;